/**
 * Google OAuth client-side handoff: redirect URI resolution and state validation.
 * Used so the callback survives sessionStorage loss (Strict Mode races, storage partitioning).
 */

import type { SignupConsent } from '../consent/types'

export const GOOGLE_OAUTH_CALLBACK_PATH = '/auth/google/callback'

export const GOOGLE_OAUTH_SESSION_STATE_KEY = 'google_oauth_state'
export const GOOGLE_OAUTH_SESSION_REDIRECT_KEY = 'google_oauth_redirect_uri'
export const GOOGLE_OAUTH_LOCAL_STATE_KEY = 'wiggin_google_oauth_state'
export const GOOGLE_OAUTH_LOCAL_REDIRECT_KEY = 'wiggin_google_oauth_redirect_uri'
/** The Create Account consent (marketing choice, Terms version), kept across the redirect. */
export const GOOGLE_OAUTH_SESSION_SIGNUP_CONSENT_KEY = 'google_oauth_signup_consent'
export const GOOGLE_OAUTH_LOCAL_SIGNUP_CONSENT_KEY = 'wiggin_google_oauth_signup_consent'
/**
 * Cloud rejects a longer `termsVersion` with 142 before it creates the user. The one definition
 * for the client: `buildSignupConsent` imports it from here, because this module has no runtime
 * imports and the consent service would otherwise pull Parse into the OAuth handoff.
 */
export const MAX_SIGNUP_TERMS_VERSION_LENGTH = 64

/** Strip trailing slashes for path comparison (not for building redirect_uri). */
export function normalizeOAuthPathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/'
}

/**
 * Prefer stored redirect_uri; if missing, derive from current location when on a known callback path.
 */
export function resolveGoogleOAuthRedirectUri(
  pathname: string,
  origin: string,
  storedRedirectUri: string | null | undefined
): string | null {
  const trimmed = storedRedirectUri?.trim()
  if (trimmed) return trimmed

  const pathNorm = normalizeOAuthPathname(pathname)
  const baseOrigin = origin.replace(/\/+$/, '')

  if (pathNorm === GOOGLE_OAUTH_CALLBACK_PATH) {
    return `${baseOrigin}${GOOGLE_OAUTH_CALLBACK_PATH}`
  }

  if (pathname.startsWith('/auth/') && pathname.includes('/callback')) {
    return `${baseOrigin}${pathNorm}`
  }

  return null
}

export type GoogleOAuthStateValidation =
  | { valid: true }
  | { valid: false; reason: 'missing_url_state' | 'missing_stored_state' | 'mismatch' }

function pickStoredState(
  sessionState: string | null,
  localState: string | null
): string | null {
  if (sessionState != null && sessionState !== '') return sessionState
  if (localState != null && localState !== '') return localState
  return null
}

/**
 * Require URL state to match value saved when the OAuth flow started (session or localStorage).
 */
export function validateGoogleOAuthReturnState(
  urlState: string | null,
  sessionState: string | null,
  localState: string | null
): GoogleOAuthStateValidation {
  if (urlState == null || urlState === '') {
    return { valid: false, reason: 'missing_url_state' }
  }
  const stored = pickStoredState(sessionState, localState)
  if (stored == null) {
    return { valid: false, reason: 'missing_stored_state' }
  }
  if (stored !== urlState) {
    return { valid: false, reason: 'mismatch' }
  }
  return { valid: true }
}

export function clearGoogleOAuthHandoffStorage(): void {
  try {
    sessionStorage.removeItem(GOOGLE_OAUTH_SESSION_STATE_KEY)
    sessionStorage.removeItem(GOOGLE_OAUTH_SESSION_REDIRECT_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(GOOGLE_OAUTH_LOCAL_STATE_KEY)
    localStorage.removeItem(GOOGLE_OAUTH_LOCAL_REDIRECT_KEY)
  } catch {
    /* ignore */
  }
  clearGoogleOAuthSignupConsent()
}

/**
 * Remove the carried sign-up choice. Every flow that is not "Sign up with Google" calls this
 * when it starts, so a value left by an abandoned sign-up can never be applied to a later
 * sign-in or account link.
 */
export function clearGoogleOAuthSignupConsent(): void {
  try {
    sessionStorage.removeItem(GOOGLE_OAUTH_SESSION_SIGNUP_CONSENT_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(GOOGLE_OAUTH_LOCAL_SIGNUP_CONSENT_KEY)
  } catch {
    /* ignore */
  }
}

/**
 * Store (or, with null, remove) the sign-up choice before redirecting to Google. Written to
 * both storages for the same reason as the state: sessionStorage can be lost on the way back.
 * The value carries the OAuth `state` of the flow that wrote it, because localStorage is shared
 * by every tab: without it, a callback in one tab could pick up another tab's choice. A flow
 * without a state stores nothing, since its callback fails the state check anyway.
 */
export function writeGoogleOAuthSignupConsent(
  consent: SignupConsent | null,
  state: string | null | undefined
): void {
  if (!consent || !state) {
    clearGoogleOAuthSignupConsent()
    return
  }
  const value = JSON.stringify({
    state,
    marketingEmailOptIn: consent.marketingEmailOptIn,
    marketingEmailWordingVersion: consent.marketingEmailWordingVersion,
    ...(consent.termsVersion ? { termsVersion: consent.termsVersion } : {}),
  })
  try {
    sessionStorage.setItem(GOOGLE_OAUTH_SESSION_SIGNUP_CONSENT_KEY, value)
  } catch {
    /* quota / private mode */
  }
  try {
    localStorage.setItem(GOOGLE_OAUTH_LOCAL_SIGNUP_CONSENT_KEY, value)
  } catch {
    /* quota / private mode */
  }
}

function parseSignupConsent(raw: string | null, urlState: string): SignupConsent | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as (Partial<SignupConsent> & { state?: unknown }) | null
    if (
      parsed?.state === urlState &&
      typeof parsed.marketingEmailOptIn === 'boolean' &&
      typeof parsed.marketingEmailWordingVersion === 'string' &&
      parsed.marketingEmailWordingVersion !== ''
    ) {
      // A bad stored version is dropped rather than failing the whole value: cloud would answer
      // 142 and stop the sign-up, while without it the user only gets the Terms screen.
      const { termsVersion } = parsed
      const validTermsVersion =
        typeof termsVersion === 'string' &&
        termsVersion !== '' &&
        termsVersion.length <= MAX_SIGNUP_TERMS_VERSION_LENGTH
      return {
        marketingEmailOptIn: parsed.marketingEmailOptIn,
        marketingEmailWordingVersion: parsed.marketingEmailWordingVersion,
        ...(validTermsVersion ? { termsVersion } : {}),
      }
    }
  } catch {
    /* corrupt value: treat as absent */
  }
  return null
}

/**
 * The sign-up choice written when this callback's flow started, session first and then the
 * localStorage backup. `urlState` is the callback's already-validated OAuth state; a stored
 * value written for any other state (another tab's flow, or a value from before the state was
 * recorded) is ignored. Null when the flow did not start on Create Account; cloud then records
 * nothing at creation and the Terms screen asks instead. A malformed value is also null, because
 * cloud rejects a malformed consent parameter before it creates the user.
 */
export function readGoogleOAuthSignupConsent(urlState: string | null): SignupConsent | null {
  if (!urlState) return null
  let fromSession: string | null = null
  let fromLocal: string | null = null
  try {
    fromSession = sessionStorage.getItem(GOOGLE_OAUTH_SESSION_SIGNUP_CONSENT_KEY)
  } catch {
    /* ignore */
  }
  try {
    fromLocal = localStorage.getItem(GOOGLE_OAUTH_LOCAL_SIGNUP_CONSENT_KEY)
  } catch {
    /* ignore */
  }
  return parseSignupConsent(fromSession, urlState) ?? parseSignupConsent(fromLocal, urlState)
}

export function writeGoogleOAuthHandoffToLocalStorage(
  state: string | undefined,
  redirectUri: string
): void {
  try {
    if (state) {
      localStorage.setItem(GOOGLE_OAUTH_LOCAL_STATE_KEY, state)
    } else {
      localStorage.removeItem(GOOGLE_OAUTH_LOCAL_STATE_KEY)
    }
    localStorage.setItem(GOOGLE_OAUTH_LOCAL_REDIRECT_KEY, redirectUri)
  } catch {
    /* quota / private mode */
  }
}
